import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ErroApi } from '../lib/api';
import { podeEscrever, useAutenticacao } from '../lib/auth';
import {
  buscarMembros,
  buscarMinisterios,
  criarFuncao,
  criarMembro,
  removerFuncao,
  type MembroLista,
  type Ministerio,
  type PendenciaSemConta,
} from '../lib/escalas';
import { gerarSenha } from '../lib/senha';
import {
  diaDaSemana,
  diaSP,
  horaSP,
  iniciais,
  mesCurto,
  numeroDoDia,
  quandoEmPalavras,
} from '../lib/datas';
import { Alerta, Botao, Cartao, SeletorBusca } from '../componentes/Interface';
import ModalConta from '../componentes/ModalConta';
import CalendarioEscalas from '../componentes/CalendarioEscalas';
import NovoEvento from '../componentes/NovoEvento';
import Layout from '../componentes/Layout';

type EventoDaEscala = {
  id: number;
  titulo: string;
  local: string | null;
  inicio: string | null;
  equipe: { nome: string; funcao: string; status: string }[];
  confirmados: number;
  total: number;
  musicas: number;
};

const CORES_AVATAR = [
  'bg-tinta-400',
  'bg-tinta-900',
  'bg-amber-600',
  'bg-sky-700',
  'bg-violet-700',
  'bg-rose-600',
];

/**
 * Quando a escala dos ministerios aparece dentro da Escala dos cultos, a
 * pagina nao tem moldura propria e o seletor ganha uma opcao para voltar
 * para as equipes dos cultos.
 */
export type Embutido = {
  chaveInicial: string;
  opcaoVoltar: { valor: string; rotulo: string };
  aoVoltar: () => void;
};

export default function Escalas({ embutido }: { embutido?: Embutido } = {}) {
  const { usuario } = useAutenticacao();
  const podeEditar = podeEscrever(usuario?.papel);

  const [ministerios, setMinisterios] = useState<Ministerio[]>([]);
  const [chave, setChave] = useState(embutido?.chaveInicial ?? 'louvor');
  const [visao, setVisao] = useState<'lista' | 'calendario'>('lista');
  const [quando, setQuando] = useState<'proximos' | 'anteriores'>('proximos');
  const [eventos, setEventos] = useState<EventoDaEscala[]>([]);
  const [membros, setMembros] = useState<MembroLista[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [criandoEvento, setCriandoEvento] = useState(false);
  const [abrirAjustes, setAbrirAjustes] = useState(false);
  const [novoMembro, setNovoMembro] = useState('');
  const [novoEmail, setNovoEmail] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  /** mostrada uma vez, para o lider anotar e entregar; nunca volta do servidor */
  const [senhaEntregar, setSenhaEntregar] = useState<{ nome: string; senha: string } | null>(null);
  const [criandoContaDe, setCriandoContaDe] = useState<{ id: number; nome: string } | null>(null);
  const [semConta, setSemConta] = useState<PendenciaSemConta[]>([]);
  const [novaFuncao, setNovaFuncao] = useState('');
  const [ocupado, setOcupado] = useState(false);
  /** a lista de ministerios ja voltou do servidor (vazia ou nao) */
  const [baseCarregada, setBaseCarregada] = useState(false);

  const ministerio = useMemo(
    () => ministerios.find((m) => m.chave === chave) ?? null,
    [ministerios, chave],
  );

  const carregarBase = useCallback(async () => {
    try {
      const mins = await buscarMinisterios();
      setMinisterios(mins.ministerios);
      if (mins.ministerios.length && !mins.ministerios.some((m) => m.chave === chave)) {
        setChave(mins.ministerios[0]!.chave);
      }
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar os ministérios.');
    } finally {
      setBaseCarregada(true);
    }
  }, [chave]);

  // So busca a escala de um ministerio que a pessoa enxerga. Antes a tela
  // pedia "louvor" de saida e quem nao serve nele (ou em nenhum) levava um
  // "sem permissao" logo ao abrir.
  const chaveValida = ministerios.some((m) => m.chave === chave);

  const carregar = useCallback(async () => {
    if (!chaveValida) return;
    try {
      const [lista, pessoas] = await Promise.all([
        api<{ eventos: EventoDaEscala[]; semConta: PendenciaSemConta[] }>(
          `/escalas/proximos?ministerio=${chave}&quando=${quando}`,
        ),
        buscarMembros(chave),
      ]);
      setEventos(lista.eventos);
      setSemConta(lista.semConta ?? []);
      setMembros(pessoas.membros);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar a escala.');
    }
  }, [chave, quando, chaveValida]);

  useEffect(() => {
    void carregarBase();
  }, [carregarBase]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const porDia = useMemo(() => {
    const mapa = new Map<string, EventoDaEscala[]>();
    for (const e of eventos) {
      if (!e.inicio) continue;
      const dia = diaSP(e.inicio);
      const lista = mapa.get(dia) ?? [];
      lista.push(e);
      mapa.set(dia, lista);
    }
    return [...mapa.entries()];
  }, [eventos]);

  const semFuncoes = Boolean(ministerio && ministerio.funcoes.length === 0);

  async function comAviso(acao: () => Promise<unknown>) {
    setOcupado(true);
    try {
      await acao();
      await carregarBase();
      await carregar();
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui salvar.');
    } finally {
      setOcupado(false);
    }
  }

  // chamada como funcao (e nao como componente) para a arvore nao remontar a cada render
  const moldura = (conteudo: ReactNode) =>
    embutido ? (
      conteudo
    ) : (
      <Layout
        titulo="Escalas"
        subtitulo="Quem serve em cada culto, com confirmação de presença."
        acoes={podeEditar && <Botao onClick={() => setCriandoEvento(true)}>+ Novo evento</Botao>}
      >
        {conteudo}
      </Layout>
    );

  return moldura(
    <>
      {erro && (
        <div className="mb-4 max-w-2xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}

      {senhaEntregar && (
        <div className="mb-4 max-w-2xl rounded-2xl bg-tinta-100 p-4 ring-1 ring-tinta-200">
          <p className="text-sm text-tinta-900">
            Senha de <strong>{senhaEntregar.nome}</strong>:{' '}
            <code className="rounded bg-white px-2 py-0.5 font-mono">{senhaEntregar.senha}</code>
          </p>
          <p className="mt-1 text-xs text-tinta-400">
            Anote e entregue agora — ela nao aparece de novo.
          </p>
          <button
            onClick={() => setSenhaEntregar(null)}
            className="mt-2 text-xs text-tinta-400 underline hover:text-tinta-900"
          >
            ja anotei
          </button>
        </div>
      )}

      {podeEditar && semConta.length > 0 && (
        <div className="mb-4 max-w-2xl rounded-2xl bg-amber-50 p-4 ring-1 ring-amber-200">
          <p className="text-sm font-semibold text-amber-900">
            {semConta.length === 1
              ? '1 pessoa escalada ainda nao tem conta'
              : `${semConta.length} pessoas escaladas ainda nao tem conta`}
          </p>
          <p className="mt-1 text-xs text-amber-800">
            Elas continuam escaladas, mas nao recebem, nao confirmam e nao avisam quando nao podem.
          </p>
          <ul className="mt-2 space-y-1">
            {semConta.map((p) => (
              <li key={p.escalaId} className="flex items-center gap-2 text-sm text-tinta-900">
                {p.nome}
                <button
                  onClick={() => setCriandoContaDe({ id: p.membroId, nome: p.nome })}
                  className="rounded-lg border border-amber-300 px-2 py-0.5 text-xs text-amber-900 hover:bg-amber-100"
                >
                  Criar conta
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {baseCarregada && ministerios.length === 0 && !erro && (
        <Cartao className="mb-4 max-w-2xl">
          <p className="text-sm text-tinta-700">
            Você ainda não está em nenhum ministério, então não há escala para mostrar aqui.
          </p>
          {usuario?.lideraEquipe && (
            <p className="mt-2 text-sm text-tinta-700">
              Como líder, a sua escala está em{' '}
              <Link to="/escala-lideres" className="font-semibold text-tinta-900 underline">
                Escala de líderes
              </Link>
              .
            </p>
          )}
        </Cartao>
      )}

      {ministerios.length > 0 && (
        <div className="mb-4 flex items-start gap-2">
          {/* sao muitos ministerios para uma fila de botoes: a lista com busca
              mostra todos sem empurrar nenhum nome para fora da tela */}
          <SeletorBusca
            className="min-w-0 max-w-xs flex-1"
            rotulo="Ministério"
            opcoes={[
              ...(embutido ? [embutido.opcaoVoltar] : []),
              ...ministerios.map((m) => ({ valor: m.chave, rotulo: m.nome })),
            ]}
            valor={chave}
            aoEscolher={(v) => (embutido && v === embutido.opcaoVoltar.valor ? embutido.aoVoltar() : setChave(v))}
            vazio="Nenhum ministério com esse nome."
          />

          {embutido && podeEditar && (
            <Botao onClick={() => setCriandoEvento(true)}>+ Novo evento</Botao>
          )}

          <div className="ml-auto flex shrink-0 rounded-xl bg-white p-1 shadow-cartao">
            {(['lista', 'calendario'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setVisao(v)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                  visao === v ? 'bg-tinta-900 text-white' : 'text-tinta-600 hover:bg-tinta-100'
                }`}
              >
                {v === 'lista' ? 'Lista' : 'Calendário'}
              </button>
            ))}
          </div>
        </div>
      )}

      {semFuncoes && podeEditar && (
        <div className="mb-4 max-w-3xl">
          <Alerta tipo="aviso">
            O <b>{ministerio?.nome}</b> está sem nenhuma função cadastrada — por isso não aparece
            onde encaixar as pessoas. Abra "Pessoas e funções" aqui embaixo e restaure as funções de
            sempre.
          </Alerta>
        </div>
      )}

      {visao === 'calendario' && ministerio && (
        <CalendarioEscalas
          ministerio={chave}
          nomeMinisterio={ministerio.nome}
          aoEscolherEvento={(id) => {
            window.location.href = `/culto/${id}`;
          }}
        />
      )}

      {visao === 'lista' && ministerios.length > 0 && (
        <>
          <div className="mb-4 flex max-w-md rounded-2xl bg-white p-1 shadow-cartao">
            {(
              [
                { valor: 'proximos', rotulo: 'Próximos' },
                { valor: 'anteriores', rotulo: 'Anteriores' },
              ] as const
            ).map((a) => (
              <button
                key={a.valor}
                onClick={() => setQuando(a.valor)}
                className={`flex-1 rounded-xl px-4 py-2 text-sm font-semibold transition ${
                  quando === a.valor ? 'bg-tinta-900 text-white' : 'text-tinta-500 hover:bg-tinta-100'
                }`}
              >
                {a.rotulo}
              </button>
            ))}
          </div>

          {eventos.length === 0 && (
            <Cartao className="max-w-3xl">
              <p className="text-sm text-tinta-400">
                {quando === 'proximos'
                  ? 'Nenhum culto marcado. Crie um com "+ Novo evento" ali em cima.'
                  : 'Nenhum culto anterior.'}
              </p>
            </Cartao>
          )}

          <div className="max-w-3xl space-y-6">
            {porDia.map(([dia, doDia]) => {
              const primeiro = doDia[0]!;
              return (
                <section key={dia}>
                  <div className="mb-2 flex items-baseline justify-between gap-3 px-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-tinta-900">
                        {numeroDoDia(primeiro.inicio!)}
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-wide text-tinta-400">
                        {mesCurto(primeiro.inicio!)}
                      </span>
                      <span className="text-sm text-tinta-400">
                        · {diaDaSemana(primeiro.inicio!)}
                      </span>
                    </div>
                    <span className="text-sm text-tinta-400">
                      {quandoEmPalavras(primeiro.inicio!)}
                    </span>
                  </div>

                  <div className="space-y-3 border-l-2 border-tinta-200 pl-3">
                    {doDia.map((e) => (
                      <Link
                        key={e.id}
                        to={`/culto/${e.id}`}
                        className="block rounded-2xl bg-white p-4 shadow-cartao transition hover:-translate-y-0.5"
                      >
                        <h3 className="text-base font-bold uppercase tracking-wide text-tinta-900">
                          {e.titulo}
                        </h3>
                        <p className="mt-0.5 text-sm text-tinta-500">
                          {horaSP(e.inicio!)}
                          {e.local ? ` · ${e.local}` : ''}
                        </p>

                        {e.equipe.length > 0 ? (
                          <div className="mt-3 flex -space-x-2">
                            {e.equipe.slice(0, 8).map((p, i) => (
                              <span
                                key={`${p.nome}-${p.funcao}`}
                                title={`${p.nome} · ${p.funcao}`}
                                className={`flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold text-white ${
                                  CORES_AVATAR[i % CORES_AVATAR.length]
                                } ${p.status === 'indisponivel' ? 'opacity-40' : ''}`}
                              >
                                {iniciais(p.nome)}
                              </span>
                            ))}
                            {e.equipe.length > 8 && (
                              <span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-tinta-100 text-[10px] font-bold text-tinta-600">
                                +{e.equipe.length - 8}
                              </span>
                            )}
                          </div>
                        ) : (
                          <p className="mt-3 text-sm font-medium text-amber-700">
                            ninguém escalado — toque para montar
                          </p>
                        )}

                        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-tinta-500">
                          <span
                            className={`font-semibold ${
                              e.total > 0 && e.confirmados < e.total
                                ? 'text-amber-600'
                                : e.total > 0
                                  ? 'text-tinta-900'
                                  : 'text-tinta-400'
                            }`}
                          >
                            👍 {e.confirmados}/{e.total}
                          </span>
                          <span>♪ {e.musicas}</span>
                          <span className="ml-auto font-semibold text-tinta-400">abrir →</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}

      {podeEditar && ministerio && (
        <div className="mt-6 max-w-3xl">
          <button
            onClick={() => setAbrirAjustes((a) => !a)}
            className="flex w-full items-center justify-between rounded-2xl bg-white px-5 py-3 text-left shadow-cartao"
          >
            <span className="font-semibold text-tinta-900">
              Pessoas e funcoes do {ministerio.nome}
            </span>
            <span className="text-sm text-tinta-400">
              {membros.length} pessoas · {ministerio.funcoes.length} funcoes {abrirAjustes ? '▾' : '▸'}
            </span>
          </button>

          {abrirAjustes && (
            <div className="mt-3 grid gap-4 md:grid-cols-2">
              <Cartao>
                <h3 className="font-semibold text-tinta-900">Pessoas</h3>
                <ul className="mt-2 space-y-1 text-sm text-tinta-700">
                  {membros.map((m) => (
                    <li key={m.id} className={m.podeEscalar ? '' : 'opacity-70'}>
                      <span>{m.nome}</span>
                      {/* quem nao pode aparece com o motivo em vez de sumir: candidato
                          que some faz o lider achar que o cadastro se perdeu */}
                      {!m.podeEscalar && (
                        <span className="ml-2 text-xs text-amber-700">
                          {m.temConta ? 'a conta dela nao serve em ministério' : 'sem conta'}
                        </span>
                      )}
                      {!m.temConta && (
                        <button
                          onClick={() => setCriandoContaDe({ id: m.id, nome: m.nome })}
                          className="ml-2 text-xs text-tinta-900 underline hover:text-tinta-900"
                        >
                          criar conta
                        </button>
                      )}
                    </li>
                  ))}
                  {membros.length === 0 && (
                    <li className="text-tinta-400">Ninguém cadastrado ainda.</li>
                  )}
                </ul>
                {/* Quem serve precisa de conta: sem ela a pessoa e escalada e nunca
                    fica sabendo. Por isso o cadastro pede e-mail e senha inicial. */}
                <div className="mt-3 space-y-2 border-t border-tinta-100 pt-3">
                  <input
                    value={novoMembro}
                    onChange={(e) => setNovoMembro(e.target.value)}
                    placeholder="Nome"
                    className="w-full rounded-xl border border-tinta-200 px-3 py-2 text-sm"
                  />
                  <input
                    value={novoEmail}
                    onChange={(e) => setNovoEmail(e.target.value)}
                    type="email"
                    placeholder="E-mail"
                    className="w-full rounded-xl border border-tinta-200 px-3 py-2 text-sm"
                  />
                  <div className="flex gap-2">
                    <input
                      value={novaSenha}
                      onChange={(e) => setNovaSenha(e.target.value)}
                      placeholder="Senha inicial"
                      className="w-full rounded-xl border border-tinta-200 px-3 py-2 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setNovaSenha(gerarSenha())}
                      className="shrink-0 rounded-xl border border-tinta-200 px-3 text-xs text-tinta-700 hover:bg-tinta-100"
                    >
                      gerar
                    </button>
                  </div>
                  <Botao
                    carregando={ocupado}
                    onClick={() => {
                      if (!novoMembro.trim() || !novoEmail.trim() || novaSenha.length < 8) {
                        setErro('Preencha nome, e-mail e uma senha de pelo menos 8 caracteres.');
                        return;
                      }
                      const nome = novoMembro.trim();
                      const senha = novaSenha;
                      void comAviso(async () => {
                        await criarMembro({
                          nome,
                          email: novoEmail.trim(),
                          senha,
                          ministerios: [chave],
                        });
                        setSenhaEntregar({ nome, senha });
                        setNovoMembro('');
                        setNovoEmail('');
                        setNovaSenha('');
                      });
                    }}
                  >
                    Cadastrar quem vai servir
                  </Botao>
                </div>
              </Cartao>

              <Cartao>
                <h3 className="font-semibold text-tinta-900">Funções</h3>
                {ministerio.funcoes.length === 0 ? (
                  <div className="mt-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-800 ring-1 ring-amber-100">
                    <p>Sem função nenhuma, não da para escalar. Reponha as de sempre:</p>
                    <Botao
                      className="mt-3"
                      carregando={ocupado}
                      onClick={() =>
                        void comAviso(() =>
                          api(`/ministerios/${ministerio.id}/funcoes/padrao`, { method: 'POST' }),
                        )
                      }
                    >
                      Restaurar funcoes do {ministerio.nome}
                    </Botao>
                  </div>
                ) : (
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {ministerio.funcoes.map((f) => (
                      <li
                        key={f.id}
                        className="flex items-center gap-1 rounded-full bg-tinta-100 px-3 py-1 text-sm text-tinta-700"
                      >
                        {f.nome}
                        <button
                          onClick={() => void comAviso(() => removerFuncao(f.id))}
                          className="text-tinta-400 hover:text-red-600"
                          title="Tirar da lista"
                        >
                          &times;
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <input
                  value={novaFuncao}
                  onChange={(e) => setNovaFuncao(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && novaFuncao.trim()) {
                      void comAviso(async () => {
                        await criarFuncao(ministerio.id, novaFuncao.trim());
                        setNovaFuncao('');
                      });
                    }
                  }}
                  placeholder="Nova função e Enter"
                  className="mt-3 w-full rounded-xl border border-tinta-200 px-3 py-2 text-sm"
                />
              </Cartao>
            </div>
          )}
        </div>
      )}

      {criandoContaDe && (
        <ModalConta
          pessoa={criandoContaDe}
          ministerio={chave}
          aoFechar={() => setCriandoContaDe(null)}
          aoCriar={(senha) => {
            setSenhaEntregar({ nome: criandoContaDe.nome, senha });
            setCriandoContaDe(null);
            void carregar();
          }}
        />
      )}

      {criandoEvento && (
        <NovoEvento
          diaSugerido={diaSP(new Date().toISOString())}
          aoFechar={() => setCriandoEvento(false)}
          aoCriar={async () => {
            setCriandoEvento(false);
            await carregar();
          }}
        />
      )}
    </>,
  );
}
